<template>
  <div id="app" :class="$route.name">
    <template v-if="userStore.fullyLoaded || !userStore.loggedIn || $route.meta?.noAuth">
      <navbar></navbar>
      <section class="main">
        <router-view />
      </section>
      <div class="notifications">
        <transition-group type="transition" name="flyout-from-right-with-fade-long" tag="ul">
          <notification-item
            v-for="notification in notificationsStore.newNotifications"
            :key="notification.id"
            :notification="notification"
            is-standalone
            @click="notificationsStore.acknowledge(notification.id)"
          />
        </transition-group>
      </div>
      <snack-bar-notifications />
    </template>

    <div class="loader" :class="{ 'is-visible': generalStore.loading && !generalStore.loadingLocked }"></div>

    <transition name="fade-in-only">
      <div v-if="generalStore.plmLoading" class="spinner-container">
        <circular-spinner large inverse />
      </div>
    </transition>

    <fs-modal ref="errorModal" is-error>
      <error-component
        :title="error.title"
        :message="error.message"
        :report="error.report"
        :stack-trace="error.stackTrace"
        :fixed="error.fixed"
        :mode="error.mode"
        class="error-modal"
        @close="errorModal?.close()"
      />
    </fs-modal>

    <bom-data-loss-confirm-modal />
    <command-palette />
    <shortcut-help-overlay />
    <chord-hint-popup />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onErrorCaptured } from 'vue';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/store/notifications';
import { useGeneral } from '@/store/general';
import { useUser } from '@/store/user';
import Navbar from './navbar/navbar.vue';
import ErrorComponent from './../error.vue';
import FsModal from '@components/modals/fs-modal.vue';
import NotificationItem from '@components/notifications/notification-item.vue';
import SnackBarNotifications from '@components/snack-bar-notifications.vue';
import BomDataLossConfirmModal from '@components/modals/bom-data-loss-confirm-modal.vue';
import ChordHintPopup from '@components/shortcuts/chord-hint-popup.vue';
import CommandPalette from '@components/shortcuts/command-palette.vue';
import ShortcutHelpOverlay from '@components/shortcuts/shortcut-help-overlay.vue';
import { useGlobalShortcuts } from './use-global-shortcuts';
import CircularSpinner from '@/components/circular-spinner.vue';
import { errorForCaptured, errorReportFor, type ErrorData } from '@lib/captured-errors';

const props = defineProps<{
  bootError?: ErrorData;
}>();

// Router and stores
const router = useRouter();
const userStore = useUser();
const notificationsStore = useNotifications();
const generalStore = useGeneral();

// Component state
const error = ref<ErrorData>({});
const errorModal = ref<InstanceType<typeof FsModal>>();

useGlobalShortcuts();

// Mounted lifecycle
onMounted(() => {
  if (props.bootError) {
    error.value = props.bootError;
    errorModal.value?.open();
  }

  router.onError((err) => {
    error.value = {
      title: 'Page could not be loaded',
      message: 'Something went wrong when loading',
      report: errorReportFor(err, errorContext()),
    };
    errorModal.value?.open();
  });
});

const errorContext = () => ({
  user: userStore.loggedIn ? userStore.userName : undefined,
  brand: userStore.company?.name,
});

// Error handling
onErrorCaptured((err, instance, info) => {
  const shown = errorForCaptured(err, instance, info, errorContext());
  if (!shown) return false;
  error.value = shown;
  errorModal.value?.open();
  // An API or user-facing error stops here; anything else also reaches the app's errorHandler.
  if (!shown.mode) return false;
});
</script>

<style lang="scss" scoped>
.error-modal {
  padding: 2rem;
  max-width: 45rem;
}
</style>

<style lang="scss">
@import '@styles/global.scss';

.spinner-container {
  position: fixed;
  padding: 0.8rem;

  background-color: rgba($c-dark, 0.8);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: 3rem;
  left: 3rem;
  z-index: 10000;
}
</style>
